import React, { useEffect, useState } from "react";
import HeaderView from "../../components/admin/headerView";
import { useTranslation } from "react-i18next";
import { APP_CONFIG } from "../../constants/constants";
import { AdminFormFieldSubmit } from "../../components/admin/formFields";
import svgIcons from "../../assets/svg/svgIcons";
import { useDispatch, useSelector } from "react-redux";
import { setModalsData, TModalsState } from "../../api/store/commonSlice";
import ModalCreateNewDashboard from "./modalCreateNewDashboard";
import CalendarIcon from "../../assets/svg/calendar-icon.svg";
import OverviewDragDropContextArea from "./overviewDragDropContextArea";
import DashboardSelectorDropdown from "./components/dashboardSelectorDropdown";
import { useOverviewContext } from "./context/overview.context";
import TimeFilterDropdown from "./components/timeFilterDropdown";
import ModalDashboardAddWidget from "./modalDashboardAddWidget";
import { useLoggedInUserData } from "../../utils/user";
import HierarchyGroupSelector from "../../components/common/hierarchyGroupSelector";
import ModalGroupSelector from "../../components/common/modalGroupSelector";
import { useOrganizationGroupsQuery } from "../../api/network/adminApiServices";
import { LegacyGroupEntityListingPayload } from "../../api/types/Group";
import { TGroupSelectorItem } from "../../components/common/groupSelector.type";
import {
  useDeleteCustomWidgetMutation,
  useDeleteDashboardMutation,
} from "../../api/network/overviewApiServices";
import ModalDeleteConfirmation from "../../components/admin/modalDeleteConfirmation";
import { toast } from "react-toastify";
import ModalUseCustomReport from "./modalUseCustomReport";
import ErrorBoundaryForSection from "../../components/errorBoundaryForSection";
import { CustomWidget } from "../../api/types/Overview";
import ModalDeleteCustomWidgetConfirmation from "../../components/admin/modalDeleteCustomWidgetConfirmation";

const ScreenOverview = () => {
  const { t } = useTranslation("translation", { keyPrefix: "overviewScreen" });
  const { t: tMngWidgets } = useTranslation("translation", {
    keyPrefix: "overviewScreen.manage_widgets",
  });
  const dispatch = useDispatch();

  // check if group hierarchy is enabled
  const accountHasGroupHierarchyEnabled = useLoggedInUserData("groupHierarchy");

  const modalsState: TModalsState = useSelector(
    (state: any) => state.commonReducer.modals
  );

  const {
    isFetchingDashboardList,
    dashList,
    refetchDashboardList,
    isUninitializedDashboardList,
    selectedDashboard,
    setSelectedDashboard,
    allTimeFilters,
    timeFilter,
    dashboardColumns,
    setAllGroupsSelected,
    selectedGroups,
    setSelectedGroups,
    widgetMarkedForDeletion,
  } = useOverviewContext();

  const userIsImpersonated = useSelector(
    (state: { commonReducer: { userIsImpersonated: boolean } }) =>
      state.commonReducer.userIsImpersonated
  );

  const [showTimeFilter, setShowTimeFilter] = useState(false);

  const thisUserOrganizationId = useLoggedInUserData("ownerOrganizationId");

  // refetch dashboard list on mount
  useEffect(() => {
    if (isUninitializedDashboardList || !dashList) return;
    refetchDashboardList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // fetch groups data
  const {
    data: dataOrgGroups,
    isFetching: isFetchingOrgGroups,
    error: errorOrgGroups,
  } = useOrganizationGroupsQuery(
    {
      accountid: `${thisUserOrganizationId}`,
      page: 1,
      page_size: APP_CONFIG.LISTINGS.XL_PAGE_SIZE, // Temp solution to show maximum groups
      search: "",
    } as LegacyGroupEntityListingPayload,
    {
      refetchOnMountOrArgChange: true,
    }
  );
  // set selected groups
  useEffect(() => {
    if (accountHasGroupHierarchyEnabled === "1") return;
    if (!!dataOrgGroups && !isFetchingOrgGroups) {
      setSelectedGroups(
        dataOrgGroups?.results?.map((group) => {
          return group.groupid;
        }) ?? ([] as string[])
      );
    }
  }, [dataOrgGroups, isFetchingOrgGroups]);
  // update all groups selected state
  useEffect(() => {
    if (accountHasGroupHierarchyEnabled === "1") return;
    if (!!dataOrgGroups && !isFetchingOrgGroups) {
      setAllGroupsSelected(
        dataOrgGroups?.results?.length === selectedGroups?.length
      );
    }
  }, [selectedGroups, dataOrgGroups, isFetchingOrgGroups]);

  const hideDropdown = () => {
    dispatch(
      setModalsData({
        ...modalsState,
        showDashboardSelectorDropdown:
          !modalsState.showDashboardSelectorDropdown,
      })
    );
  };

  useEffect(() => {
    return () => {
      dispatch(
        setModalsData({
          ...modalsState,
          showDashboardSelectorDropdown: false,
          showDashboardAddWidget: false,
          showHierarchyGroupSelector: false,
          showGroupSelector: false,
        })
      );
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAddRemoveWidgets = () => {
    dispatch(
      setModalsData({
        ...modalsState,
        isExpandedSideMenu: false,
        showDashboardAddWidget: !modalsState.showDashboardAddWidget,
      })
    );
  };

  const totalWidgetsOnDashboard =
    dashboardColumns?.col1?.colWidgets?.length +
    dashboardColumns?.col2?.colWidgets?.length +
    dashboardColumns?.col3?.colWidgets?.length;

  // delete dashboard
  const [deleteDashboardAPITrigger, { isLoading: isLoadingDeleteDashboard }] =
    useDeleteDashboardMutation();
  const [dashboardIdForDelete, setDashboardIdForDelete] = useState<
    number | null
  >(null);
  const handleDeleteDashboard = () => {
    // select new dashboard if the current one is deleted
    if (dashboardIdForDelete === selectedDashboard) {
      setSelectedDashboard(
        dashList?.find((d) => d.id !== dashboardIdForDelete)?.id
      );
    }
    // call delete dashboard api
    if (dashboardIdForDelete)
      deleteDashboardAPITrigger(dashboardIdForDelete)
        .unwrap()
        .then(() => {
          toast.success(t("toast.dashboard_delete_success"));
        });
  };

  // attempt to close print mode on window focus - i.e. when the browser's print popup closes
  useEffect(() => {
    const handleWindowFocus = () => {
      if (APP_CONFIG.DEBUG.ALL) console.log("window focus");
      // Disabling auto hide of All modals on screen in order to keep Create custom widget modal open on focus
      if (!!modalsState.widgetSelectedForPrint) {
        dispatch(
          setModalsData({ ...modalsState, widgetSelectedForPrint: null })
        );
      }
    };
    window.addEventListener("focus", handleWindowFocus);
    return () => {
      window.removeEventListener("focus", handleWindowFocus);
    };
  }, []);

  const [
    deleteCustomWidgetAPITrigger,
    { isLoading: isLoadingDeleteCustomWidget },
  ] = useDeleteCustomWidgetMutation();
  const handleDeleteCustomWidget = (widget: CustomWidget) => {
    // to be implemented
    if (APP_CONFIG.DEBUG.ALL)
      console.log("delete widget", widgetMarkedForDeletion?.id);
    if (!!widgetMarkedForDeletion?.id) {
      deleteCustomWidgetAPITrigger(widgetMarkedForDeletion?.id)
        .unwrap()
        .then(() => {
          toast.success(tMngWidgets("toast.delete_widget_success"));
        })
        .catch(() => {
          toast.error(tMngWidgets("toast.delete_widget_failed"));
        });
    }
  };

  if (typeof dashList === "undefined" && isFetchingDashboardList) {
    return (
      <div
        className={`flex items-center justify-center ${userIsImpersonated ? "h-[calc(100vh-96px-40px)]" : "h-[calc(100vh-96px)]"}`}
      >
        <img
          src={svgIcons.LoadingAnimation}
          alt="loader"
          className="size-10 animate-spin"
        />
      </div>
    );
  }

  return (
    <>
      {/* Group Filter Popup */}
      {accountHasGroupHierarchyEnabled === "1" && false ? (
        <>
          {/* 01/24/2025 Behaviour changed - clicking on Group/Asset Filter Button opens the group/asset popup which further opens the hierarchy group selector */}
          {/* {modalsState.showHierarchyGroupSelector && (
            <HierarchyGroupSelector
              initiallySelectedGroups={selectedGroups}
              onSelectionChange={setSelectedGroups}
            />
          )} */}
        </>
      ) : (
        <>
          {!!dataOrgGroups && !isFetchingOrgGroups && (
            <ModalGroupSelector
              hideGroupAssetToggle={false}
              allGroups={dataOrgGroups?.results ?? []}
              filteredGroupData={
                !!dataOrgGroups && !isFetchingOrgGroups
                  ? (dataOrgGroups?.results?.map((group) => ({
                      ...group,
                      is_hgroup: false,
                      checked: (selectedGroups as string[]).includes(
                        group.groupid
                      ),
                    })) as TGroupSelectorItem[])
                  : []
              }
              setFilteredGroupData={(e: TGroupSelectorItem[]) => {
                if (APP_CONFIG.DEBUG.ALL)
                  console.log("setFilteredGroupData", e);
                return setSelectedGroups(
                  e
                    ?.filter((group) => group.checked)
                    ?.map((group) => group.groupid)
                );
              }}
              key={`group-selector-${isFetchingOrgGroups.toString()}`}
            />
          )}
        </>
      )}

      {/* Print Mode Overlay */}
      <div
        className={`fixed top-0 left-0 w-full h-screen bg-white/70 backdrop-blur-[1px] z-popup-overlay${!!modalsState.widgetSelectedForPrint ? "" : " hidden"}`}
        onClick={() => {
          dispatch(
            setModalsData({ ...modalsState, widgetSelectedForPrint: null })
          );
        }}
      />

      {/* Header */}
      {!!dashList && dashList?.length > 0 && (
        <HeaderView
          title={
            <span
              className={`relative${modalsState.showDashboardAddWidget || !!modalsState.widgetSelectedForPrint ? " opacity-50 pointer-events-none touch-none" : ""}`}
            >
              {/* Dashboard Selector */}
              <button
                className={`flex items-center gap-2 relative${modalsState.showDashboardSelectorDropdown ? " z-popup" : ""}`}
                onClick={hideDropdown}
              >
                {dashList?.find((d) => d.id === selectedDashboard)?.name}
                <img
                  src={svgIcons.ChevronRight}
                  alt=""
                  className={`invert size-7 ${modalsState.showDashboardSelectorDropdown ? "-rotate-90" : "rotate-90"}`}
                />
              </button>
              {modalsState.showDashboardSelectorDropdown && (
                <>
                  <div
                    className="fixed top-0 left-0 w-full h-screen bg-white/70 backdrop-blur-[1px] z-popup-overlay"
                    onClick={hideDropdown}
                  ></div>
                  <DashboardSelectorDropdown
                    isLoadingDeleteDashboard={isLoadingDeleteDashboard}
                    setDashboardIdForDelete={setDashboardIdForDelete}
                    onSelection={() => {
                      // dispatch(setModalsData({ ...modalsState, showDashboardSelectorDropdown: false }))
                    }}
                  />
                </>
              )}
            </span>
          }
          rightComponent={
            <div className="flex items-center gap-2">
              {modalsState.showDashboardAddWidget ||
              !!modalsState.widgetSelectedForPrint ? null : (
                <>
                  {/* Time Filter */}
                  <span className="relative">
                    <button
                      className={`flex items-center gap-2 ps-4 pr-2 h-10 rounded-lg relative ${showTimeFilter ? "bg-accent-blue-pale z-popup" : "hover:bg-accent-blue-pale"}`}
                      onClick={() => setShowTimeFilter(!showTimeFilter)}
                    >
                      <CalendarIcon className="size-6" />
                      <span className="text-field-info-text">
                        {
                          allTimeFilters?.find((tf) => tf.slug === timeFilter)
                            ?.title
                        }
                      </span>
                      <img
                        className="size-6 rotate-90 -ml-1"
                        src={svgIcons.ChevronRight}
                        alt="chevron right"
                      />
                    </button>
                    {showTimeFilter && (
                      <>
                        <div
                          className="fixed top-0 left-0 w-full h-screen bg-white/70 backdrop-blur-[1px] z-popup-overlay"
                          onClick={() => setShowTimeFilter(!showTimeFilter)}
                        ></div>
                        <TimeFilterDropdown
                          onSelection={() => {
                            setShowTimeFilter(!showTimeFilter);
                          }}
                        />
                      </>
                    )}
                  </span>

                  {/* Filter Groups */}
                  <button
                    className={`flex items-center gap-2 px-4 h-10 rounded-lg hover:bg-accent-blue-pale mr-2`}
                    onClick={() => {
                      setShowTimeFilter(false);
                      dispatch(
                        setModalsData({
                          ...modalsState,
                          showDashboardAddWidget: false,
                          showDashboardSelectorDropdown: false,
                          // 01/24/2025 Behaviour changed - clicking on Group/Asset Filter Button opens the group/asset popup which further opens the hierarchy group selector
                          // ...(accountHasGroupHierarchyEnabled === "1" && false
                          //   ? { showHierarchyGroupSelector: true }
                          //   : {
                          showGroupSelector: true,
                          // }),
                        })
                      );
                    }}
                  >
                    <img src={svgIcons.FilterIcon} alt="filter" />
                    <span className="text-accent-blue-dark">
                      {t("filter_groups")}
                    </span>
                  </button>
                </>
              )}

              {/* Add/Remove Widgets */}
              {!modalsState.widgetSelectedForPrint && (
                <span className="relative">
                  <AdminFormFieldSubmit
                    customWrapperClass={
                      modalsState.showDashboardAddWidget
                        ? "relative z-modal opacity-0"
                        : ""
                    }
                    type="button"
                    variant="primary-like"
                    label={
                      <span className="flex justify-center whitespace-nowrap">
                        {t(
                          modalsState.showDashboardAddWidget
                            ? "done"
                            : "add_remove_widgets"
                        )}
                        <img
                          className="ml-2"
                          src={
                            modalsState.showDashboardAddWidget
                              ? svgIcons.CheckedIcon
                              : svgIcons.ButtonIconPlus
                          }
                          alt={
                            modalsState.showDashboardAddWidget
                              ? "done"
                              : "add / remove widgets"
                          }
                        />
                      </span>
                    }
                    onClick={handleAddRemoveWidgets}
                  />
                  {modalsState.showDashboardAddWidget && (
                    <ModalDashboardAddWidget />
                  )}
                </span>
              )}
            </div>
          }
          wrapperClassName={`no-print flex justify-between ${APP_CONFIG.DES.DASH.P_HORIZ} py-2`}
        />
      )}
      <ModalCreateNewDashboard />

      {/* Main Content */}
      <div className={`${APP_CONFIG.DES.DASH.P_HORIZ} py-2 relative`}>
        {/* <DashboardSelector /> */}

        {dashList?.length === 0 ? (
          <div
            className={`flex flex-col items-center justify-center ${userIsImpersonated ? "h-[calc(100vh-96px-40px)]" : "h-[calc(100vh-96px)]"}`}
          >
            <span className="text-heading-gray888 font-bold text-2xl">
              {t("no_dashboard")}
            </span>
            <span className="text-heading-gray888 opacity-70 text-sm mt-2 text-center w-96">
              {t("no_dashboard_subtitle")}
            </span>
            <AdminFormFieldSubmit
              customWrapperClass="mt-8"
              label={t("no_dashboard")}
              variant="primary"
              onClick={() => {
                dispatch(
                  setModalsData({ ...modalsState, showCreateDashboard: true })
                );
              }}
            />
          </div>
        ) : (
          <>
            {totalWidgetsOnDashboard === 0 ? (
              <div
                className={`flex flex-col items-center justify-center ${userIsImpersonated ? "h-[calc(100vh-96px-40px)]" : "h-[calc(100vh-96px)]"}`}
              >
                <span className="text-heading-gray888 font-bold text-2xl">
                  {t("no_widget_on_dashboard")}
                </span>
                <span className="text-heading-gray888 opacity-70 text-sm mt-2 text-center w-96">
                  {t("no_widget_on_dashboard_subtitle")}
                </span>
                <AdminFormFieldSubmit
                  customWrapperClass={`mt-8${modalsState.showDashboardAddWidget ? " invisible" : ""}`}
                  label={t("add_a_widget")}
                  variant="primary"
                  onClick={handleAddRemoveWidgets}
                />
              </div>
            ) : (
              <OverviewDragDropContextArea />
            )}
          </>
        )}
      </div>
      <>
        <ModalDeleteConfirmation handleDeleteAdmin={handleDeleteDashboard} />
        {!!widgetMarkedForDeletion && (
          <ModalDeleteCustomWidgetConfirmation
            handleDeleteAdmin={handleDeleteCustomWidget}
            widget={widgetMarkedForDeletion}
          />
        )}
        {modalsState.showUseCustomReport && (
          <ErrorBoundaryForSection>
            <ModalUseCustomReport />
          </ErrorBoundaryForSection>
        )}
      </>
    </>
  );
};

export default ScreenOverview;
